import React, { useEffect, useMemo, useState } from 'react';
import { X, FileText, FileSpreadsheet, Download, Loader2 } from 'lucide-react';
import api from '../services/api';
import { useNotificationContext } from '../contexts/NotificationContext';

const SECCIONES = [
    { id: 'resumen', titulo: 'Resumen general', desc: 'Total, asignados, disponibles y otros estados' },
    { id: 'distribucion', titulo: 'Distribución del inventario', desc: 'Barra por estado y gráficos por tipo y empresa' },
    { id: 'empresas', titulo: 'Disponibilidad por empresa', desc: 'Tabla con totales y % disponible' },
    { id: 'calidad', titulo: 'Identificación y calidad del registro', desc: 'Series vacías, repetidas y códigos únicos' },
    { id: 'estados', titulo: 'Estados que requieren aclaración', desc: 'Equipos que no están Asignados ni Disponibles' },
    { id: 'acciones', titulo: 'Acciones recomendadas', desc: 'Pendientes generados a partir de los hallazgos' },
    { id: 'valores', titulo: 'Valor del inventario', desc: 'Valor total, promedio y desglose por categoría y empresa' },
    { id: 'disponibles', titulo: 'Detalle de equipos disponibles', desc: 'Listado completo para asignar' },
    { id: 'listado', titulo: 'Listado completo de activos', desc: 'Equipos, celulares, monitores, red y más, agrupados por categoría' },
    { id: 'insumos', titulo: 'Insumos y consumibles', desc: 'Stock actual, mínimo y estado de cada insumo' },
];

const TODAS = SECCIONES.map((s) => s.id);

export default function ReporteInventarioModal({ open, onClose }) {
    const { alertError, alertSuccess } = useNotificationContext();
    const [secciones, setSecciones] = useState(TODAS);
    const [empresas, setEmpresas] = useState([]);
    const [empresasSel, setEmpresasSel] = useState([]);
    const [formato, setFormato] = useState('pdf');
    const [generando, setGenerando] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get('/reportes/inventario-ti/empresas')
            .then((r) => { setEmpresas(r.data || []); setEmpresasSel(r.data || []); })
            .catch(() => setEmpresas([]));
    }, [open]);

    const todasMarcadas = secciones.length === TODAS.length;
    const alternar = (id) => setSecciones((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    const alternarEmpresa = (e) => setEmpresasSel((s) => (s.includes(e) ? s.filter((x) => x !== e) : [...s, e]));
    const puedeGenerar = useMemo(() => secciones.length > 0 && (empresas.length === 0 || empresasSel.length > 0) && !generando, [secciones, empresas, empresasSel, generando]);

    if (!open) return null;

    const generar = async () => {
        setGenerando(true);
        try {
            const todasEmpresas = empresasSel.length === empresas.length;
            const res = await api.post('/reportes/inventario-ti', {
                secciones,
                empresas: todasEmpresas ? [] : empresasSel,
                formato,
            }, { responseType: 'blob' });
            const tipo = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            const url = URL.createObjectURL(new Blob([res.data], { type: tipo }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Reporte_Inventario_TI_${new Date().toISOString().slice(0, 10)}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            alertSuccess('Reporte generado');
            onClose();
        } catch (e) {
            alertError('No se pudo generar el reporte. Intenta de nuevo.');
        } finally {
            setGenerando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
            <div role="dialog" aria-label="Generar reporte de inventario TI"
                className="bg-white dark:bg-gray-800 w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Reporte de inventario TI</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Elige qué puntos incluir. Se genera con los datos actuales del inventario.</p>
                    </div>
                    <button onClick={onClose} aria-label="Cerrar" className="p-1 text-gray-400 hover:text-gray-600"><X size={20} /></button>
                </div>

                <div className="p-5 overflow-y-auto space-y-5">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Puntos del reporte</h3>
                            <button className="text-xs text-blue-600 hover:underline" onClick={() => setSecciones(todasMarcadas ? [] : TODAS)}>
                                {todasMarcadas ? 'Quitar todos' : 'Marcar todos'}
                            </button>
                        </div>
                        <div className="space-y-1.5">
                            {SECCIONES.map((s) => (
                                <label key={s.id} className="flex items-start gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                    <input type="checkbox" className="mt-1 h-4 w-4" checked={secciones.includes(s.id)} onChange={() => alternar(s.id)} />
                                    <span>
                                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{s.titulo}</span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400">{s.desc}</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {empresas.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Empresas incluidas</h3>
                            <div className="flex flex-wrap gap-2">
                                {empresas.map((e) => (
                                    <button key={e} type="button" onClick={() => alternarEmpresa(e)}
                                        className={`px-3 py-1.5 rounded-full text-sm border ${empresasSel.includes(e) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                        {e}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Formato</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {[{ id: 'pdf', t: 'PDF', i: FileText }, { id: 'excel', t: 'Excel', i: FileSpreadsheet }].map(({ id, t, i: Icono }) => (
                                <button key={id} type="button" onClick={() => setFormato(id)}
                                    className={`flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-medium ${formato === id ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-600 text-blue-700 dark:text-blue-300' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                    <Icono size={16} /> {t}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">Cancelar</button>
                    <button onClick={generar} disabled={!puedeGenerar}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {generando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                        {generando ? 'Generando…' : `Descargar ${formato === 'pdf' ? 'PDF' : 'Excel'}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
